import { Suspense, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import ErrorBoundary from "./Components/ErrorBoundary";
import Loader from './Components/Loader';
import OfflineDialog from "./Components/OfflineDialog";
import ScrollToTop from "./Components/ScrollToTop";
import InternalMicrospecDynamicUIProvider from "./GlobalProvider";
import ApplicationRoutes from "./Helpers/Routes";
import { debugCSP, initializeCSP, SecurityMonitor } from "./Security/XSSProtection";
import MicrospecThemeProvider from "./Themes";
import { getOrDefault } from "./Helpers/Utils";

const MicrospecDynamicUI = ({ config }) => {
  // Initialize security and performance measures
  useEffect(() => {
    // Initialize Content Security Policy
    initializeCSP();

    // Debug CSP after a short delay to ensure it's applied
    setTimeout(() => {
      if (process.env.NODE_ENV === 'development') {
        debugCSP();
      }
    }, 100);

    // Report initialization
    SecurityMonitor.reportViolation('APP_INIT', 'MicrospecDynamicUI initialized', {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      location: window.location.href
    });

    // Set up global error handler
    const handleGlobalError = (event) => {
      SecurityMonitor.reportViolation('GLOBAL_ERROR', event.error?.message || 'Unknown error', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error?.stack
      });
    };

    const handleUnhandledRejection = (event) => {
      SecurityMonitor.reportViolation('UNHANDLED_REJECTION', event.reason?.message || 'Unknown rejection', {
        reason: event.reason?.stack || event.reason
      });
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const providers = getOrDefault(config.contextsProviders, []);

  // Nest all custom context providers
  const renderWithProviders = (children) => {
    return providers.reduceRight((acc, Provider) => {
      return <Provider config={config}>{acc}</Provider>;
    }, children);
  };

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <MicrospecThemeProvider config={config}>
          <InternalMicrospecDynamicUIProvider config={config}>
            <Suspense  fallback={<Loader />}>
              {renderWithProviders(
                <>
                  <ScrollToTop />
                  <OfflineDialog config={config} />
                  <ApplicationRoutes config={config} />
                </>
              )}
            </Suspense>
          </InternalMicrospecDynamicUIProvider>
        </MicrospecThemeProvider >
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default MicrospecDynamicUI;
